import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, Check, X, Clock, RefreshCw, Send, Truck, Filter, Calendar, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

/**
 * Reusable Incoming Consignment component for all sections
 * @param {string} sectionName - The section name (e.g., "Greige", "FOB", "Goods Transfer")
 * @param {string} sectionDisplayName - Display name for the section (optional, defaults to sectionName)
 * @param {string} backRoute - Route to navigate back to (e.g., "/sections/greige")
 */
const SectionIncomingConsignment = ({ sectionName, sectionDisplayName, backRoute }) => {
  const navigate = useNavigate();
  const [consignments, setConsignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectDialog, setRejectDialog] = useState({ open: false, entryId: null });
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(null);
  const [activeTab, setActiveTab] = useState('pending');
  // 2026-06 — Receipt-only (Document / Trims) parcels: bundle receipt only,
  // never enter operations.
  const [receiptOnly, setReceiptOnly] = useState([]);
  const [roProcessing, setRoProcessing] = useState(null);
  const [reworkDialog, setReworkDialog] = useState({ open: false, item: null });
  const [reworkMode, setReworkMode] = useState('reassort_only');
  
  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [sourceSiteFilter, setSourceSiteFilter] = useState('');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  const [sourceSites, setSourceSites] = useState([]);

  const displayName = sectionDisplayName || sectionName;

  // Fetch unique source sites for the dropdown
  const fetchSourceSites = async () => {
    try {
      // Get unique sources from both consignments and internal transfers
      const response = await apiClient.get(`/consignments/section/${encodeURIComponent(sectionName)}/incoming`);
      const allConsignments = response.data.consignments || [];
      
      const sources = new Set();
      allConsignments.forEach(c => {
        if (c.vendor_name) sources.add(c.vendor_name);
        if (c.from_section) sources.add(c.from_section);
        if (c.source_site) sources.add(c.source_site);
      });
      
      setSourceSites(Array.from(sources).filter(Boolean).sort());
    } catch (error) {
      console.error('Error fetching source sites:', error);
    }
  };

  const fetchConsignments = async () => {
    try {
      setLoading(true);
      
      // Build query params
      const params = new URLSearchParams();
      if (sourceSiteFilter) params.append('source_site', sourceSiteFilter);
      if (dateFromFilter) params.append('date_from', dateFromFilter);
      if (dateToFilter) params.append('date_to', dateToFilter);
      
      const queryString = params.toString();
      const url = `/consignments/section/${encodeURIComponent(sectionName)}/incoming${queryString ? '?' + queryString : ''}`;
      
      const response = await apiClient.get(url);
      
      const allConsignments = (response.data.consignments || []).map(c => ({
        ...c,
        source_type: c.type === 'internal_transfer' ? 'internal' : 'external',
        display_id: c.document_barcode || c.transfer_id || c.entry_id,
        // Ensure vendor_name is set properly
        vendor_name: c.vendor_name || (c.from_section ? `${c.from_section.replace(/\s+section$/i, '')} Section` : 'Unknown')
      }));
      
      // Sort by date descending
      allConsignments.sort((a, b) => 
        new Date(b.created_at || b.documentation_confirmed_at || 0) - 
        new Date(a.created_at || a.documentation_confirmed_at || 0)
      );
      
      setConsignments(allConsignments);
    } catch (error) {
      toast.error('Failed to fetch consignments');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSourceSites();
  }, [sectionName]);

  useEffect(() => {
    fetchConsignments();
  }, [sectionName, sourceSiteFilter, dateFromFilter, dateToFilter]);

  // 2026-06 — Receipt-only (Document / Trims) parcels for this section.
  const fetchReceiptOnly = async () => {
    try {
      const params = new URLSearchParams({ parcel_scope: 'receipt_only' });
      const resp = await apiClient.get(`/consignments/section/${encodeURIComponent(sectionName)}/incoming?${params.toString()}`);
      setReceiptOnly(resp.data.consignments || []);
    } catch (error) {
      console.error('Error fetching receipt-only parcels:', error);
    }
  };

  useEffect(() => {
    fetchReceiptOnly();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionName]);

  const handleReceiptOnlyComplete = async (item) => {
    try {
      setRoProcessing(item.entry_id);
      const resp = await apiClient.post(
        `/consignments/section/${encodeURIComponent(sectionName)}/receipt-only/complete/${item.entry_id}`,
        {}
      );
      toast.success(resp?.data?.message || 'Receipt confirmed (receipt only)');
      fetchReceiptOnly();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm receipt');
    } finally {
      setRoProcessing(null);
    }
  };

  const clearFilters = () => {
    setSourceSiteFilter('');
    setDateFromFilter('');
    setDateToFilter('');
  };

  const hasActiveFilters = sourceSiteFilter || dateFromFilter || dateToFilter;

  const isReceived = (c) => ['received', 'accepted'].includes(c.status);
  const pendingList = consignments.filter((c) => !isReceived(c));
  const receivedList = consignments.filter(isReceived);
  const visibleConsignments = activeTab === 'received' ? receivedList : pendingList;

  const handleRequest = async (item) => {
    try {
      setProcessing(item.entry_id);
      
      if (item.source_type === 'internal') {
        // Internal transfer - use request-consignment endpoint
        await apiClient.post('/internal-transfer/request-consignment', {
          transfer_id: item.transfer_id || item.entry_id
        });
        toast.success('Consignment requested - Source section notified to ship');
      } else {
        // External consignment - use existing endpoint
        await apiClient.post(`/consignments/section/${encodeURIComponent(sectionName)}/incoming/${item.entry_id}/request`);
        toast.success('Consignment requested - waiting for shipment confirmation');
      }
      
      fetchConsignments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request');
    } finally {
      setProcessing(null);
    }
  };

  const handleAccept = async (item, reworkModeArg) => {
    try {
      setProcessing(item.entry_id);

      if (item.source_type === 'internal') {
        // 2026-05: REWORK transfers fully retire the source bundle and push
        // its USNs into the Inhouse Rework Pool. Use the dedicated endpoint.
        // 2026-06: the operator now picks how the rework is processed —
        // "reassort_only" (straight to Assortment) or "full_reprocess"
        // (Folding → Pressing → Assortment → Damage Checking again).
        if ((item.transfer_type || '').toUpperCase() === 'REWORK') {
          const tid = item.transfer_id || item.entry_id;
          const res = await apiClient.post(
            `/internal-transfer/${tid}/receive-for-rework`,
            { rework_mode: reworkModeArg || 'reassort_only' },
          );
          toast.success(
            res?.data?.message
            || `Rework received — ${res?.data?.usns_to_rework_pool ?? 0} USNs in pool.`
          );
        } else {
          // Standard internal transfer accept
          await apiClient.post('/internal-transfer/accept', {
            transfer_id: item.transfer_id || item.entry_id
          });
          toast.success('Transfer accepted - Ready for bundle receipt');
        }
      } else {
        // External consignment
        await apiClient.post(`/consignments/section/${encodeURIComponent(sectionName)}/incoming/${item.entry_id}/accept`);
        toast.success('Consignment accepted - Ready for bundle receipt');
      }

      fetchConsignments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept');
    } finally {
      setProcessing(null);
    }
  };

  const openReworkDialog = (item) => {
    setReworkMode('reassort_only');
    setReworkDialog({ open: true, item });
  };

  const confirmReworkReceive = async () => {
    const item = reworkDialog.item;
    const mode = reworkMode;
    setReworkDialog({ open: false, item: null });
    if (item) {
      await handleAccept(item, mode);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    try {
      const item = consignments.find(c => c.entry_id === rejectDialog.entryId);
      setProcessing(rejectDialog.entryId);
      
      if (item?.source_type === 'internal') {
        // Internal transfer rejection
        await apiClient.post('/internal-transfer/reject', {
          transfer_id: item.transfer_id || rejectDialog.entryId,
          rejection_reason: rejectReason
        });
      } else {
        // External consignment rejection
        await apiClient.post(
          `/consignments/section/${encodeURIComponent(sectionName)}/incoming/${rejectDialog.entryId}/reject`,
          null,
          { params: { reason: rejectReason } }
        );
      }
      
      toast.success('Consignment rejected');
      setRejectDialog({ open: false, entryId: null });
      setRejectReason('');
      fetchConsignments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    } finally {
      setProcessing(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      ready: { color: 'bg-yellow-100 text-yellow-700', icon: Clock, label: 'Ready - Not Requested' },
      created: { color: 'bg-yellow-100 text-yellow-700', icon: Clock, label: 'Ready - Not Requested' },
      requested: { color: 'bg-orange-100 text-orange-700', icon: Send, label: 'Requested - Awaiting Shipment' },
      shipped: { color: 'bg-blue-100 text-blue-700', icon: Truck, label: 'Shipped - Pending Receipt' },
      received: { color: 'bg-green-100 text-green-700', icon: Check, label: 'Received' },
      rejected: { color: 'bg-red-100 text-red-700', icon: X, label: 'Rejected' }
    };
    const config = statusConfig[status] || statusConfig.ready;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} border-0`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const renderActions = (consignment) => {
    const status = consignment.status;
    const entryId = consignment.entry_id;
    const isInternal = consignment.source_type === 'internal';
    
    if (status === 'ready' || status === 'created') {
      return (
        <div className="flex gap-3">
          <Button
            onClick={() => handleRequest(consignment)}
            disabled={processing === entryId}
            className="bg-orange-500 hover:bg-orange-600"
            data-testid={`request-${entryId}`}
          >
            <Send className="w-4 h-4 mr-2" />
            Request Consignment
          </Button>
          <Button
            variant="outline"
            onClick={() => setRejectDialog({ open: true, entryId })}
            disabled={processing === entryId}
            className="border-red-300 text-red-600 hover:bg-red-50"
            data-testid={`reject-${entryId}`}
          >
            <X className="w-4 h-4 mr-2" />
            Reject
          </Button>
        </div>
      );
    }
    
    if (status === 'requested') {
      return (
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="text-orange-600 border-orange-300">
            <Clock className="w-3 h-3 mr-1" />
            {isInternal ? 'Waiting for source section to ship' : 'Waiting for Inward to confirm shipment'}
          </Badge>
        </div>
      );
    }
    
    if (status === 'shipped') {
      const isRework = (consignment.transfer_type || '').toUpperCase() === 'REWORK';
      return (
        <div className="flex gap-3">
          <Button
            onClick={() => isRework ? openReworkDialog(consignment) : handleAccept(consignment)}
            disabled={processing === entryId}
            className={isRework ? 'bg-amber-600 hover:bg-amber-700' : 'bg-[#2D5F4F] hover:bg-[#1A1F2E]'}
            data-testid={`accept-${entryId}`}
          >
            <Check className="w-4 h-4 mr-2" />
            {isRework ? 'Receive for Rework' : 'Accept & Receive'}
          </Button>
          <Button
            variant="outline"
            onClick={() => setRejectDialog({ open: true, entryId })}
            disabled={processing === entryId}
            className="border-red-300 text-red-600 hover:bg-red-50"
            data-testid={`reject-${entryId}`}
          >
            <X className="w-4 h-4 mr-2" />
            Reject
          </Button>
        </div>
      );
    }
    
    return null;
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate(backRoute)} 
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Inward Consignment</h1>
              <p className="text-sm text-[#E8DCC8]">{displayName} Section</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className={`border-white/20 text-white hover:bg-white/10 ${hasActiveFilters ? 'bg-white/20' : ''}`}
              data-testid="filter-button"
            >
              <Filter className={`w-4 h-4 mr-2`} />
              Filters {hasActiveFilters && `(${[sourceSiteFilter, dateFromFilter, dateToFilter].filter(Boolean).length})`}
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={fetchConsignments}
              className="border-white/20 text-white hover:bg-white/10"
              data-testid="refresh-button"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      {/* Filter Panel */}
      {showFilters && (
        <div className="bg-white border-b border-[#E5DFD0] shadow-sm">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Source Site Filter */}
              <div>
                <Label className="text-sm text-[#5A5F6E] mb-1 flex items-center gap-1">
                  <Building2 className="w-3 h-3" />
                  Source Site / Vendor
                </Label>
                <Select value={sourceSiteFilter || "all"} onValueChange={(val) => setSourceSiteFilter(val === "all" ? "" : val)}>
                  <SelectTrigger className="w-full" data-testid="source-site-filter">
                    <SelectValue placeholder="All Sources" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sources</SelectItem>
                    {sourceSites.map((site) => (
                      <SelectItem key={site} value={site}>{site}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Date From Filter */}
              <div>
                <Label className="text-sm text-[#5A5F6E] mb-1 flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  From Date
                </Label>
                <Input 
                  type="date" 
                  value={dateFromFilter}
                  onChange={(e) => setDateFromFilter(e.target.value)}
                  className="w-full"
                  data-testid="date-from-filter"
                />
              </div>
              
              {/* Date To Filter */}
              <div>
                <Label className="text-sm text-[#5A5F6E] mb-1 flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  To Date
                </Label>
                <Input 
                  type="date" 
                  value={dateToFilter}
                  onChange={(e) => setDateToFilter(e.target.value)}
                  className="w-full"
                  data-testid="date-to-filter"
                />
              </div>
              
              {/* Clear Filters Button */}
              <div className="flex items-end">
                <Button 
                  variant="outline" 
                  onClick={clearFilters}
                  disabled={!hasActiveFilters}
                  className="w-full"
                  data-testid="clear-filters-button"
                >
                  <X className="w-4 h-4 mr-2" />
                  Clear Filters
                </Button>
              </div>
            </div>
            
            {/* Active Filters Summary */}
            {hasActiveFilters && (
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <span className="text-sm text-[#5A5F6E]">Active filters:</span>
                {sourceSiteFilter && (
                  <Badge variant="secondary" className="text-xs">
                    Source: {sourceSiteFilter}
                    <button onClick={() => setSourceSiteFilter('')} className="ml-1 hover:text-red-500">×</button>
                  </Badge>
                )}
                {dateFromFilter && (
                  <Badge variant="secondary" className="text-xs">
                    From: {dateFromFilter}
                    <button onClick={() => setDateFromFilter('')} className="ml-1 hover:text-red-500">×</button>
                  </Badge>
                )}
                {dateToFilter && (
                  <Badge variant="secondary" className="text-xs">
                    To: {dateToFilter}
                    <button onClick={() => setDateToFilter('')} className="ml-1 hover:text-red-500">×</button>
                  </Badge>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Pending / Received tabs */}
        <div className="flex items-center gap-2 mb-6 border-b border-[#E5DFD0]">
          <button
            onClick={() => setActiveTab('pending')}
            data-testid="tab-pending"
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === 'pending' ? 'border-[#2D5F4F] text-[#2D5F4F]' : 'border-transparent text-[#5A5F6E] hover:text-[#1A1F2E]'}`}
          >
            Pending
            <span className={`ml-2 text-xs rounded-full px-2 py-0.5 ${activeTab === 'pending' ? 'bg-[#2D5F4F] text-white' : 'bg-gray-100 text-[#5A5F6E]'}`} data-testid="tab-pending-count">{pendingList.length}</span>
          </button>
          <button
            onClick={() => setActiveTab('received')}
            data-testid="tab-received"
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === 'received' ? 'border-[#2D5F4F] text-[#2D5F4F]' : 'border-transparent text-[#5A5F6E] hover:text-[#1A1F2E]'}`}
          >
            Received Slips
            <span className={`ml-2 text-xs rounded-full px-2 py-0.5 ${activeTab === 'received' ? 'bg-[#2D5F4F] text-white' : 'bg-gray-100 text-[#5A5F6E]'}`} data-testid="tab-received-count">{receivedList.length}</span>
          </button>
          <button
            onClick={() => setActiveTab('receipt_only')}
            data-testid="tab-receipt-only"
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === 'receipt_only' ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-[#5A5F6E] hover:text-[#1A1F2E]'}`}
          >
            Receipt Only (Docs / Samples / Trims)
            <span className={`ml-2 text-xs rounded-full px-2 py-0.5 ${activeTab === 'receipt_only' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-[#5A5F6E]'}`} data-testid="tab-receipt-only-count">{receiptOnly.filter(c => !c.bundle_receipt_completed).length}</span>
          </button>
        </div>

        {activeTab === 'receipt_only' ? (
          <div className="grid gap-4" data-testid="receipt-only-list">
            <div className="text-xs text-[#5A5F6E] bg-indigo-50 border border-indigo-100 rounded-md px-3 py-2">
              Documents (invoices / swatch cards), Samples and Trims only need a <b>bundle receipt</b> to confirm they reached this section. They do <b>not</b> enter any operation queue.
            </div>
            {receiptOnly.length === 0 ? (
              <Card className="border-[#E5DFD0]">
                <CardContent className="py-12 text-center">
                  <Package className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-semibold text-[#1A1F2E] mb-2">No Document / Trims parcels</h3>
                  <p className="text-[#5A5F6E]">Nothing pending a receipt-only confirmation for {displayName} section.</p>
                </CardContent>
              </Card>
            ) : (
              receiptOnly.map((c) => {
                const done = !!c.bundle_receipt_completed;
                const pType = (c.parcel_type || '').toLowerCase();
                const pLabel = pType === 'trims' ? 'Trims' : (pType === 'sample' ? 'Sample' : 'Document');
                return (
                  <Card key={c.entry_id} className="border-[#E5DFD0]" data-testid={`receipt-only-${c.entry_id}`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg font-display flex items-center gap-2">
                          {c.document_barcode || c.entry_id}
                          <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded font-medium" data-testid={`receipt-only-type-${c.entry_id}`}>{pLabel}</span>
                        </CardTitle>
                        {done ? (
                          <Badge className="bg-green-100 text-green-700 border-0"><Check className="w-3 h-3 mr-1" />Received</Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700 border-0"><Clock className="w-3 h-3 mr-1" />Awaiting Receipt</Badge>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <div>
                          <p className="text-xs text-[#5A5F6E]">Vendor / Customer</p>
                          <p className="font-medium text-[#1A1F2E]">{c.vendor_name || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-[#5A5F6E]">Doc No</p>
                          <p className="font-medium text-[#1A1F2E]">{c.vendor_doc_no || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-[#5A5F6E]">Bundles</p>
                          <p className="font-medium text-[#1A1F2E]">{c.bundle_count || c.bundles?.length || 0}</p>
                        </div>
                        <div>
                          <p className="text-xs text-[#5A5F6E]">Created At</p>
                          <p className="font-medium text-[#1A1F2E]">{c.created_at ? new Date(c.created_at).toLocaleString('en-IN') : '-'}</p>
                        </div>
                      </div>
                      {done ? (
                        <div className="text-sm text-green-700">
                          Received on {c.bundle_receipt_completed_at ? new Date(c.bundle_receipt_completed_at).toLocaleString('en-IN') : '-'}
                          {c.bundle_receipt_completed_by ? ` by ${c.bundle_receipt_completed_by}` : ''}
                        </div>
                      ) : (
                        <Button
                          onClick={() => handleReceiptOnlyComplete(c)}
                          disabled={roProcessing === c.entry_id}
                          className="bg-indigo-600 hover:bg-indigo-700"
                          data-testid={`receipt-only-confirm-${c.entry_id}`}
                        >
                          <Check className="w-4 h-4 mr-2" />
                          Confirm Bundle Receipt
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-[#2D5F4F]" />
          </div>
        ) : visibleConsignments.length === 0 ? (
          <Card className="border-[#E5DFD0]">
            <CardContent className="py-12 text-center">
              <Package className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-[#1A1F2E] mb-2">
                {activeTab === 'received' ? 'No Received Slips' : 'No Pending Consignments'}
              </h3>
              <p className="text-[#5A5F6E]">
                {activeTab === 'received'
                  ? `No consignments have been received yet for ${displayName} section`
                  : `There are no pending consignments for ${displayName} section`}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {visibleConsignments.map((consignment) => (
              <Card key={consignment.entry_id} className="border-[#E5DFD0]" data-testid={`consignment-${consignment.entry_id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-display">{consignment.document_barcode || consignment.entry_id}</CardTitle>
                    {getStatusBadge(consignment.status)}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className={`grid grid-cols-2 ${consignment.usn_count != null ? 'md:grid-cols-5' : 'md:grid-cols-4'} gap-4 mb-4`}>
                    <div>
                      <p className="text-xs text-[#5A5F6E]">Entry ID</p>
                      <p className="font-medium text-[#1A1F2E]">{consignment.entry_id}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#5A5F6E]">Bundles</p>
                      <p className="font-medium text-[#1A1F2E]" data-testid={`bundle-count-${consignment.entry_id}`}>
                        {consignment.bundle_count || consignment.bundles?.length || 0}
                      </p>
                    </div>
                    {consignment.usn_count != null && (
                      <div>
                        <p className="text-xs text-[#5A5F6E]">USNs</p>
                        <p className="font-medium text-[#1A1F2E]" data-testid={`usn-count-${consignment.entry_id}`}>
                          {consignment.usn_count}
                        </p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-[#5A5F6E]">Vendor</p>
                      <p className="font-medium text-[#1A1F2E]">{consignment.vendor_name || consignment.source_section || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#5A5F6E]">
                        {consignment.status === 'shipped' ? 'Shipped At' : consignment.status === 'requested' ? 'Requested At' : 'Created At'}
                      </p>
                      <p className="font-medium text-[#1A1F2E]">
                        {consignment.shipped_at 
                          ? new Date(consignment.shipped_at).toLocaleString('en-IN')
                          : consignment.requested_at
                            ? new Date(consignment.requested_at).toLocaleString('en-IN')
                            : consignment.created_at
                              ? new Date(consignment.created_at).toLocaleString('en-IN')
                              : '-'}
                      </p>
                    </div>
                  </div>
                  
                  {renderActions(consignment)}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog.open} onOpenChange={(open) => !open && setRejectDialog({ open: false, entryId: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Consignment</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="reason">Rejection Reason</Label>
            <Input
              id="reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter reason for rejection..."
              className="mt-2"
              data-testid="reject-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog({ open: false, entryId: null })}>
              Cancel
            </Button>
            <Button onClick={handleReject} className="bg-red-600 hover:bg-red-700" data-testid="confirm-reject-button">
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rework Mode Dialog */}
      <Dialog open={reworkDialog.open} onOpenChange={(open) => !open && setReworkDialog({ open: false, item: null })}>
        <DialogContent data-testid="rework-mode-dialog">
          <DialogHeader>
            <DialogTitle>Receive for Rework</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <p className="text-sm text-[#5A5F6E] mb-3">
              How should these reworked pieces be processed?
            </p>
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => setReworkMode('reassort_only')}
                data-testid="rework-mode-reassort"
                className={`w-full text-left rounded-lg border p-3 transition-colors ${reworkMode === 'reassort_only' ? 'border-[#2D5F4F] bg-[#2D5F4F]/5 ring-1 ring-[#2D5F4F]' : 'border-[#E5DFD0] hover:border-[#2D5F4F]/50'}`}
              >
                <div className="flex items-center gap-2">
                  <span className={`w-3 h-3 rounded-full border ${reworkMode === 'reassort_only' ? 'bg-[#2D5F4F] border-[#2D5F4F]' : 'border-gray-400'}`} />
                  <span className="font-medium text-[#1A1F2E]">Re-assortment only</span>
                </div>
                <p className="text-xs text-[#5A5F6E] mt-1 ml-5">
                  Pieces skip Folding/Pressing/Damage and go straight to Assortment Packing for re-packing.
                </p>
              </button>
              <button
                type="button"
                onClick={() => setReworkMode('full_reprocess')}
                data-testid="rework-mode-full"
                className={`w-full text-left rounded-lg border p-3 transition-colors ${reworkMode === 'full_reprocess' ? 'border-[#2D5F4F] bg-[#2D5F4F]/5 ring-1 ring-[#2D5F4F]' : 'border-[#E5DFD0] hover:border-[#2D5F4F]/50'}`}
              >
                <div className="flex items-center gap-2">
                  <span className={`w-3 h-3 rounded-full border ${reworkMode === 'full_reprocess' ? 'bg-[#2D5F4F] border-[#2D5F4F]' : 'border-gray-400'}`} />
                  <span className="font-medium text-[#1A1F2E]">Full re-processing</span>
                </div>
                <p className="text-xs text-[#5A5F6E] mt-1 ml-5">
                  Pieces re-enter the full pipeline: Folding → Pressing → Assortment → Damage Checking, exactly like a fresh piece.
                </p>
              </button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReworkDialog({ open: false, item: null })} data-testid="rework-cancel-button">
              Cancel
            </Button>
            <Button onClick={confirmReworkReceive} className="bg-amber-600 hover:bg-amber-700" data-testid="rework-confirm-button">
              <Check className="w-4 h-4 mr-2" />
              Receive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SectionIncomingConsignment;
